import React, { useState } from 'react';
import useSEO from '../components/SEO';
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Search, Loader2, User, BookOpen, GraduationCap, CheckCircle2, AlertCircle } from 'lucide-react';
import { getStudentByMatricule, Student } from '../api/students';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';

const SearchStudent: React.FC = () => {
    useSEO({
        title: "Recherche - ISTMECH",
        description: "Recherche des étudiants de l'Institut Supérieur de Technologie Médicale du Cap-Haïtien (ISTMECH).",
        keywords: "ISTMECH, recherche, étudiants, Cap-Haïtien",
        url: "https://www.istmech.edu.ht/recherche",
        image: "https://www.istmech.edu.ht/og-image.png"
    });

    const [matricule, setMatricule] = useState('');
    const [student, setStudent] = useState<Student | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!matricule.trim()) return;

        setLoading(true);
        setError(null);
        setStudent(null);


        try {
            const data = await getStudentByMatricule(matricule);
            setStudent(data);
        } catch (err: any) {
            console.error(err);
            if (err.response && err.response.status === 404) {
                setError("Aucun étudiant trouvé avec ce matricule.");
            } else {
                setError("Une erreur est survenue lors de la recherche. Veuillez réessayer.");
            }
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'actif':
                return <Badge className="bg-green-500 hover:bg-green-600">Actif</Badge>;
            case 'inactif':
                return <Badge variant="secondary">Inactif</Badge>;
            case 'diplome':
                return <Badge className="bg-blue-500 hover:bg-blue-600">Diplômé</Badge>;
            case 'abandonne':
                return <Badge variant="destructive">Abandonné</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    return (
        <div id="recherche" className="scroll-mt-24 w-full max-w-4xl mx-auto px-4 py-8">
            <div className="text-center mb-10 space-y-4">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Recherche Étudiant</h1>
                <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                    Entrez le numéro de matricule pour vérifier le statut d'un étudiant.
                </p>
            </div>

            <Card className="shadow-lg border-t-4 border-t-blue-600">
                <CardHeader>
                    <CardTitle>Rechercher un étudiant</CardTitle>
                    <CardDescription>Saisissez le matricule complet (ex: 2023-IST-001)</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSearch} className="flex gap-4 items-center">
                        <Input
                            placeholder="Entrez le matricule..."
                            value={matricule}
                            onChange={(e) => setMatricule(e.target.value)}
                            className="flex-1 text-lg py-6"
                        />
                        <Button type="submit" disabled={loading || !matricule.trim()} size="lg" className="px-8">
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                            <span className="ml-2 hidden sm:inline">Rechercher</span>
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <div className="mt-8 transition-all duration-300 ease-in-out">
                {loading && (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
                    </div>
                )}

                {!loading && error && (
                    <Alert variant="destructive" className="animate-in fade-in slide-in-from-bottom-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Erreur</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {!loading && student && (
                    <Card className="overflow-hidden animate-in fade-in slide-in-from-bottom-8 border-l-4 border-l-green-500 shadow-md">
                        <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 p-6 md:p-8">
                            <div className="flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
                                <div className="shrink-0">
                                    {student.photo ? (
                                        <img
                                            src={student.photo}
                                            alt={`${student.prenom} ${student.nom}`}
                                            className="h-32 w-32 rounded-full object-cover border-4 border-white shadow-lg"
                                        />
                                    ) : (
                                        <div className="h-32 w-32 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center border-4 border-white shadow-lg">
                                            <User className="h-16 w-16 text-blue-600 dark:text-blue-300" />
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-4 flex-1 w-full">
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                            {student.prenom} {student.nom}
                                        </h2>
                                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                                            Matricule: {student.matricule}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                                        <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                                            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-full">
                                                <GraduationCap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div className="text-left">
                                                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Filière</p>
                                                <p className="font-medium">{student.filiere}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                                            <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-full">
                                                <BookOpen className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                            </div>
                                            <div className="text-left">
                                                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Niveau</p>
                                                <p className="font-medium">Année {student.niveau}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 sm:col-span-2 md:col-span-1">
                                            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-full">
                                                <CheckCircle2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                            </div>
                                            <div className="text-left">
                                                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Statut</p>
                                                <div className="mt-1">{getStatusBadge(student.statut)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
};
export default SearchStudent;
